import { useEffect, useState, useCallback, useRef } from 'react';
import { useTour } from './TourContext';
import { getTourConfig } from './tourConfigs';
import generalApi from '../../generalApi';
import { getDecodedToken } from '../../utils/auth';

export const useTourManager = () => {
  const { startTour } = useTour();
  const [tourStatus, setTourStatus] = useState({
    hasCompletedTour: false,
    loading: true,
    error: null
  });
  
  // Prevent multiple API calls and tour initiations
  const hasCheckedTourStatus = useRef(false);
  const hasTourBeenInitiated = useRef(false);

  // FIXED: Memoize checkTourStatus to prevent recreation on every render
  const checkTourStatus = useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (hasCheckedTourStatus.current) {
      console.log('Tour status already checked, skipping duplicate call');
      return;
    }

    // FIXED: Only check tour status if user is authenticated
    const token = getDecodedToken();
    if (!token) {
      console.log('User not authenticated, setting default tour status');
      // User not logged in - set safe defaults and don't make API call
      setTourStatus({
        hasCompletedTour: false,
        loading: false,
        error: null
      });
      hasCheckedTourStatus.current = true;
      return;
    }

    try {
      hasCheckedTourStatus.current = true; // Mark as checked before API call
      setTourStatus(prev => ({ ...prev, loading: true, error: null }));
      
      console.log('Fetching tour status from API...');
      const response = await generalApi.get('/auth/tour-status');
      
      if (response.data.success) {
        console.log('Tour status received:', response.data);
        setTourStatus({
          hasCompletedTour: response.data.hasCompletedTour,
          loading: false,
          error: null
        });
      }
    } catch (error) {
      console.error('Error checking tour status:', error);
      setTourStatus({
        hasCompletedTour: false,
        loading: false,
        error: error.message
      });
    }
  }, []); // Empty dependency array since it only depends on API calls

  // FIXED: Only check tour status on mount if there's a token
  useEffect(() => {
    const token = getDecodedToken();
    if (token) {
      checkTourStatus();
    } else {
      // User not authenticated - set safe defaults immediately
      setTourStatus({
        hasCompletedTour: false,
        loading: false,
        error: null
      });
      hasCheckedTourStatus.current = true;
    }
  }, []); // Empty dependency array - only run on mount

  // FIXED: Memoize startLanguageDashboardTour to prevent recreation
  const startLanguageDashboardTour = useCallback(() => {
    const config = getTourConfig('language-dashboard');
    if (config) {
      console.log('Starting language dashboard tour');
      startTour(config);
    }
  }, [startTour]);

  // FIXED: Memoize shouldShowTourForNewUser
  const shouldShowTourForNewUser = useCallback(() => {
    // Check if this is a new user who hasn't completed the tour
    const token = getDecodedToken();
    if (!token) return false;

    const shouldShow = !tourStatus.hasCompletedTour && !tourStatus.loading;
    console.log('Should show tour for new user:', shouldShow, {
      hasCompletedTour: tourStatus.hasCompletedTour,
      loading: tourStatus.loading
    });
    return shouldShow;
  }, [tourStatus.hasCompletedTour, tourStatus.loading]);

  // FIXED: Memoize initializeNewUserTour
  const initializeNewUserTour = useCallback(() => {
    // Prevent multiple tour initiations
    if (hasTourBeenInitiated.current) {
      console.log('Tour already initiated, skipping duplicate initialization');
      return;
    }

    // Auto-start tour for new users after a short delay
    if (shouldShowTourForNewUser()) {
      console.log('Initializing tour for new user');
      hasTourBeenInitiated.current = true;
      
      setTimeout(() => {
        startLanguageDashboardTour();
      }, 1000); // 1 second delay to let the dashboard load
    }
  }, [shouldShowTourForNewUser, startLanguageDashboardTour]);

  // NEW: Automatically initiate tour when conditions are met
  useEffect(() => {
    // Only try to initiate tour after tour status has been loaded
    if (!tourStatus.loading && !tourStatus.error) {
      initializeNewUserTour();
    }
  }, [tourStatus.loading, tourStatus.error, initializeNewUserTour]);

  // FIXED: Memoize resetTourForTesting
  const resetTourForTesting = useCallback(async () => {
    // Only allow tour reset if user is authenticated
    const token = getDecodedToken();
    if (!token) {
      console.warn('Cannot reset tour - user not authenticated');
      return false;
    }

    try {
      await generalApi.post('/auth/reset-tour');
      setTourStatus(prev => ({ ...prev, hasCompletedTour: false }));
      
      // Reset the flags to allow tour to be initiated again
      hasTourBeenInitiated.current = false;
      hasCheckedTourStatus.current = false;
      
      return true;
    } catch (error) {
      console.error('Error resetting tour:', error);
      return false;
    }
  }, []);

  return {
    tourStatus,
    startLanguageDashboardTour,
    shouldShowTourForNewUser,
    initializeNewUserTour,
    resetTourForTesting,
    checkTourStatus
  };
};